import { useCallback, useMemo, useState } from 'react';
import { Form, Input, message, Modal, Tabs, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  ExportOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildFixedAssetsColumns } from './fixedAssetsColumns';
import { fixedAssetsApi } from './fixedAssetsApi';
import { friendlyErrorFor } from './fixedAssetsHelpers';
import FixedAssetModal from './FixedAssetModal';
import FixedAssetDepreciationModal from './FixedAssetDepreciationModal';
import FixedAssetDisposalModal from './FixedAssetDisposalModal';
import FixedAssetCategoriesPanel from './FixedAssetCategoriesPanel';
import '../accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Assets Register page (Stage 6B.9D).
//
// Two-tab page:
//   1. Assets       — grid + Add/Edit/View + Depreciate/Dispose/Delete
//   2. Categories   — simple master list
//
// Approval-MFA L1 is wired for Delete / Depreciate / Dispose. The
// engine (voucherPosting.service) is the only code path that mutates
// ledger balances — depreciation and disposal both delegate to it via
// the backend service.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => fixedAssetsApi.list(query) };

function defaultOnError(err) {
  message.error({ content: friendlyErrorFor(err), key: 'fa-list' });
}

function AssetsTab() {
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildFixedAssetsColumns({ currentPage, pageSize }),
    [currentPage, pageSize]);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [modalOpen, setModalOpen]   = useState(false);
  const [modalMode, setModalMode]   = useState('add');
  const [modalRecord, setModalRecord] = useState(null);
  const [deprOpen, setDeprOpen]     = useState(false);
  const [deprAsset, setDeprAsset]   = useState(null);
  const [dispOpen, setDispOpen]     = useState(false);
  const [dispAsset, setDispAsset]   = useState(null);

  const openAdd = useCallback(() => {
    setModalMode('add'); setModalRecord(null); setModalOpen(true);
  }, []);

  const openWithFetch = useCallback(async (record, modeKind) => {
    try {
      const full = await fixedAssetsApi.get(record.id);
      setModalMode(modeKind);
      setModalRecord(full);
      setModalOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const openEdit = useCallback((r) => openWithFetch(r, 'edit'), [openWithFetch]);
  const openView = useCallback((r) => openWithFetch(r, 'view'), [openWithFetch]);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  // ── Depreciate ──────────────────────────────────────────
  const handleDepreciate = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active assets can be depreciated. This asset is ${record.status}.`);
      return;
    }
    try {
      const full = await fixedAssetsApi.get(record.id);
      setDeprAsset(full);
      setDeprOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onDepreciated = useCallback(async () => {
    setDeprOpen(false);
    await refetch();
  }, [refetch]);

  // ── Dispose ─────────────────────────────────────────────
  const handleDispose = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active assets can be disposed. This asset is ${record.status}.`);
      return;
    }
    try {
      const full = await fixedAssetsApi.get(record.id);
      setDispAsset(full);
      setDispOpen(true);
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, []);
  const onDisposed = useCallback(async () => {
    setDispOpen(false);
    await refetch();
  }, [refetch]);

  // ── Delete ──────────────────────────────────────────────
  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'active') {
      message.warning(`Only active assets without posted depreciation can be deleted. This asset is ${record.status}.`);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete asset ${record.assetCode}?`,
        icon:  <ExclamationCircleOutlined />,
        content: 'The asset will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm. Assets with posted depreciation cannot be deleted — dispose them instead.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.fixed_asset.delete',
        resourceType:   'fixed_asset',
        resourceId:     record.id,
        operationLabel: 'Delete fixed asset',
        resourceLabel:  `${record.assetCode} · ${record.assetName}`,
        action: (approvalMfaChallengeId) =>
          fixedAssetsApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Asset deleted.');
      await refetch();
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(friendlyErrorFor(e));
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',       label: 'View',        icon: <EyeOutlined />,        onClick: openView },
    { key: 'edit',       label: 'Edit',        icon: <EditOutlined />,       onClick: openEdit },
    { key: 'depreciate', label: 'Depreciate',  icon: <CalculatorOutlined />, onClick: handleDepreciate },
    { key: 'dispose',    label: 'Dispose',     icon: <ExportOutlined />,     onClick: handleDispose },
    { key: 'delete',     label: 'Delete',      icon: <DeleteOutlined />,
      danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handleDepreciate, handleDispose, handleDelete]);

  return (
    <>
      <ErpDataGrid
        title="Fixed Assets"
        subtitle="Manage company assets, depreciation and disposal"
        gridId="accounts-fixed-assets-v1"
        columns={columns}
        rowKey="id"
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={56}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by asset code, name, location, serial…"
        searchShortcut
        exportable
        exportFilename="fixed-assets"
        actions={actionsProp}
        addButtonText="New Asset"
        emptyTitle="No fixed assets yet"
        emptyText="Click New Asset to register your first fixed asset."
        emptyCtaLabel="New Asset"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        contextMenuActions={contextMenuActions}
        {...gridHandlers}
      />
      <FixedAssetModal
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={closeModal}
        onSaved={onSaved}
      />
      <FixedAssetDepreciationModal
        open={deprOpen}
        asset={deprAsset}
        onClose={() => setDeprOpen(false)}
        onPosted={onDepreciated}
        runWithApproval={runWithApproval}
      />
      <FixedAssetDisposalModal
        open={dispOpen}
        asset={dispAsset}
        onClose={() => setDispOpen(false)}
        onDisposed={onDisposed}
        runWithApproval={runWithApproval}
      />
      {ApprovalMfaModalSlot}
    </>
  );
}

export default function FixedAssetsPage() {
  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <Tabs
          size="small"
          defaultActiveKey="assets"
          items={[
            { key: 'assets',     label: 'Assets',     children: <AssetsTab /> },
            { key: 'categories', label: 'Categories', children: <FixedAssetCategoriesPanel /> }
          ]}
        />
      </div>
    </ErpMasterShell>
  );
}
